// components/RepliesPage/RepliesPageContainer.tsx
import { useState, useEffect, useMemo } from "react";
import { respondToMessage } from "@/api/twitter";
import { updatePersonaValues } from "@/api/personas";
import { Container } from "@/components/Container";
import { useToast } from "@/hooks/use-toast";
import {
  usePendingRepliesQuery,
  useSkippedRepliesQuery,
  useSpamRepliesData,
  useRepliedMessagesQuery,
} from "@/api/twitter";
import { useQueryClient } from "@tanstack/react-query";

// Import subcomponents
import ConfigurationCard from "./ConfigurationCard";
import RepliesLoadingDialog from "./dialogs/RepliesLoadingDialog";
import HelpModal from "./dialogs/HelpModal";
import ProcessTweetModal from "./dialogs/ProcessTweetModal";
import ReplyRulesModal from "./dialogs/ReplyRulesModal";
import ReplyTabs from "./ReplyTabs";

interface RepliesPageContainerProps {
  personaId: string;
  personaData: any;
}

function RepliesPageContainer({
  personaId,
  personaData,
}: RepliesPageContainerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // State management
  const [tweetIdToProcess, setTweetIdToProcess] = useState("");
  const [processingTweet, setProcessingTweet] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("replied");

  // Modal states
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showReplyRulesModal, setShowReplyRulesModal] = useState(false);

  // Loading state management
  const [currentLoadingStep, setCurrentLoadingStep] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState<string[]>([]);
  const [loadingInterval, setLoadingInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  // Get the current manual approval setting from personaData
  const twitterData = personaData?.twitter_data || {};
  const [automatedReplies, setAutomatedReplies] = useState(
    twitterData.automated_replies !== false, // Default to true if not set
  );

  // Initialize reply rules from personaData or with defaults
  const [replyRules, setReplyRules] = useState(() => {
    return (
      twitterData.reply_rules || {
        enable_moderation: false,
        min_followers: { enabled: false, value: 0 },
        max_followers: { enabled: false, value: 10000 },
        min_account_age_days: { enabled: false, value: 30 },
        max_replies_per_hour: { enabled: false, value: 10 },
        max_replies_per_day: { enabled: false, value: 50 },
        custom_rules: "",
      }
    );
  });

  // Load reply rules from persona data when it changes
  useEffect(() => {
    if (personaData?.twitter_data?.reply_rules) {
      setReplyRules(personaData.twitter_data.reply_rules);
    }
    setAutomatedReplies(personaData?.twitter_data.automated_replies);
  }, [personaData]);

  // Query for replied messages
  const {
    data: repliedMessagesData,
    isLoading: isRepliedMessagesLoading,
    refetch: refetchRepliedMessages,
  } = useRepliedMessagesQuery(personaId, 20, activeTab === "replied");

  // Query for pending replies
  const {
    data: pendingRepliesData,
    isLoading: isPendingRepliesLoading,
    refetch: refetchPendingReplies,
  } = usePendingRepliesQuery(personaId, 20, activeTab === "pending");

  // Query for skipped replies
  const {
    data: skippedRepliesData,
    isLoading: isSkippedRepliesLoading,
    refetch: refetchSkippedReplies,
  } = useSkippedRepliesQuery(
    personaId,
    20,
    activeTab === "skipped" || activeTab === "spam",
  );

  // Filter spam replies from skipped replies
  const spamRepliesData = useSpamRepliesData(skippedRepliesData);

  // Get filtered Skipped Replies (excluding spam)
  const filteredSkippedReplies = useMemo(() => {
    if (!skippedRepliesData || !skippedRepliesData.data) return { data: [] };

    // Filter out messages that are spam
    const nonSpamReplies = skippedRepliesData.data.filter(
      (reply) =>
        !(
          reply.status === "spam" ||
          reply.metadata?.rule_violated === "spam_detection"
        ),
    );

    return { data: nonSpamReplies };
  }, [skippedRepliesData]);

  // Sort the replies based on sortBy option
  const sortedReplies = useMemo(() => {
    let data: any[] = [];

    if (activeTab === "pending" && pendingRepliesData?.data) {
      data = [...pendingRepliesData.data];
    } else if (activeTab === "skipped" && filteredSkippedReplies?.data) {
      data = [...filteredSkippedReplies.data];
    } else if (activeTab === "spam" && spamRepliesData?.data) {
      data = [...spamRepliesData.data];
    } else if (activeTab === "replied" && repliedMessagesData?.data) {
      data = [...repliedMessagesData.data];
    }

    return data.sort((a, b) => {
      // For replied messages, we might want to sort by responded_at instead of created_at
      const aDate =
        activeTab === "replied" && a.responded_at
          ? new Date(a.responded_at).getTime()
          : new Date(a.created_at).getTime();

      const bDate =
        activeTab === "replied" && b.responded_at
          ? new Date(b.responded_at).getTime()
          : new Date(b.created_at).getTime();

      if (sortBy === "newest") {
        return bDate - aDate;
      } else {
        return aDate - bDate;
      }
    });
  }, [
    pendingRepliesData,
    filteredSkippedReplies,
    spamRepliesData,
    repliedMessagesData,
    sortBy,
    activeTab,
  ]);

  // Loading indicator management
  const startLoadingMessages = (messages: string[]) => {
    setLoadingMessages(messages);
    setCurrentLoadingStep(0);
    setIsLoading(true);
    const interval = setInterval(() => {
      setCurrentLoadingStep((prev) => (prev + 1) % messages.length);
    }, 10000);
    setLoadingInterval(interval);
  };

  const stopLoadingMessages = () => {
    if (loadingInterval) clearInterval(loadingInterval);
    setIsLoading(false);
    setCurrentLoadingStep(0);
    setLoadingMessages([]);
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (loadingInterval) clearInterval(loadingInterval);
    };
  }, [loadingInterval]);

  // Refresh all replies
  const refreshReplies = async () => {
    try {
      setIsRefreshing(true);

      if (activeTab === "pending") {
        await queryClient.invalidateQueries({
          queryKey: ["pending-replies", personaId],
        });
        await refetchPendingReplies();
        toast({ description: "Pending replies refreshed" });
      } else if (activeTab === "skipped" || activeTab === "spam") {
        // Both skipped and spam tabs use the skipped replies data
        await queryClient.invalidateQueries({
          queryKey: ["skipped-replies", personaId],
        });
        await refetchSkippedReplies();
        toast({
          description: `${activeTab === "skipped" ? "Skipped" : "Spam"} replies refreshed`,
        });
      } else if (activeTab === "replied") {
        await queryClient.invalidateQueries({
          queryKey: ["replied-messages", personaId],
        });
        await refetchRepliedMessages();
        toast({ description: "Replied messages refreshed" });
      }
    } catch (error) {
      console.error(`Error refreshing ${activeTab} replies:`, error);
      toast({
        description: `Failed to refresh ${activeTab} replies`,
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Toggle the automated replies setting
  const toggleAutomatedReplies = async () => {
    const newValue = !automatedReplies;

    const loadingMessages = [
      "Updating agent configuration...",
      "Configuring Twitter reply workflow...",
      "Setting up reply rules...",
      "Finalizing settings...",
    ];

    try {
      startLoadingMessages(loadingMessages);

      // Update the persona values in the backend
      await updatePersonaValues(personaId, {
        "twitter_data.automated_replies": newValue,
      });

      // Update local state
      setAutomatedReplies(newValue);

      // Refresh data whether switching to manual or automated mode
      await refreshReplies();

      toast({
        description: `Twitter replies will now be ${newValue ? "automated" : "manually approved"}.`,
      });
    } catch (error) {
      console.error("Error updating automated replies setting:", error);
      toast({
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      stopLoadingMessages();
    }
  };

  // Save the reply moderation rules
  const saveReplyRules = async (updatedRules: any) => {
    const loadingMessages = [
      "Updating reply moderation rules...",
      "Configuring Twitter engagement filters...",
      "Setting up safety parameters...",
      "Finalizing moderation settings...",
    ];

    try {
      startLoadingMessages(loadingMessages);

      // Update the persona values in the backend
      await updatePersonaValues(personaId, {
        "twitter_data.reply_rules": updatedRules,
      });

      // Close the modal
      setShowReplyRulesModal(false);

      // Update local state
      setReplyRules(updatedRules);

      toast({
        description: "Reply moderation rules updated successfully.",
      });
    } catch (error) {
      console.error("Error updating reply rules:", error);
      toast({
        description: "Failed to update reply rules. Please try again.",
        variant: "destructive",
      });
    } finally {
      stopLoadingMessages();
    }
  };

  // Process a specific tweet
  const processTweet = async (tweetId: string) => {
    if (!tweetId.trim()) {
      toast({ description: "Please enter a tweet ID" });
      return;
    }

    const loadingMessages = [
      "Fetching tweet details...",
      "Analyzing conversation context...",
      "Generating personalized response...",
      "Processing message...",
    ];

    try {
      setProcessingTweet(true);
      startLoadingMessages(loadingMessages);

      const response = await respondToMessage(personaId, tweetId);

      if (response.automated) {
        toast({ description: "Tweet processed and automatically posted" });
      } else {
        toast({ description: "Tweet processed and added to pending queue" });
        // Refresh the pending replies list
        await refreshReplies();
      }

      // Clear the input and close the modal
      setTweetIdToProcess("");
      setShowProcessModal(false);
    } catch (error) {
      console.error("Error processing tweet:", error);
      toast({
        description: "Failed to process tweet. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingTweet(false);
      stopLoadingMessages();
    }
  };

  // Handle approve/reject callbacks
  const handleApprove = () => {
    refreshReplies(); // Refresh the list after approving
  };

  const handleReject = () => {
    refreshReplies(); // Refresh the list after rejecting
  };

  // Check if data is loading based on active tab
  const isRepliesLoading = () => {
    if (activeTab === "pending") return isPendingRepliesLoading;
    if (activeTab === "skipped" || activeTab === "spam")
      return isSkippedRepliesLoading;
    if (activeTab === "replied") return isRepliedMessagesLoading;
    return false;
  };

  return (
    <Container className="max-w-5xl px-4 sm:px-6 mx-auto">
      {/* Loading Dialog */}
      <RepliesLoadingDialog
        isOpen={isLoading}
        message={loadingMessages[currentLoadingStep] || "Processing..."}
        onClose={stopLoadingMessages}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Process Tweet Modal */}
      <ProcessTweetModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        onProcess={processTweet}
        tweetId={tweetIdToProcess}
        onChange={setTweetIdToProcess}
        isProcessing={processingTweet}
      />

      {/* Reply Moderation Rules Modal */}
      <ReplyRulesModal
        isOpen={showReplyRulesModal}
        onClose={() => setShowReplyRulesModal(false)}
        replyRules={replyRules}
        onSave={saveReplyRules}
      />

      {/* Main content */}
      <div className="space-y-4">
        {/* Configuration Card */}
        <ConfigurationCard
          automatedReplies={automatedReplies}
          onToggleAutomatedReplies={toggleAutomatedReplies}
          onShowHelp={() => setShowHelpModal(true)}
          onShowProcessModal={() => setShowProcessModal(true)}
          onShowReplyRules={() => setShowReplyRulesModal(true)}
          isLoading={isLoading}
        />

        {/* Tabs for Replied, Pending, Skipped, and Spam Replies */}
        <ReplyTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isRefreshing={isRefreshing}
          isRepliesLoading={isRepliesLoading()}
          onRefresh={refreshReplies}
          sortedReplies={sortedReplies}
          isRepliedMessagesLoading={isRepliedMessagesLoading}
          isPendingRepliesLoading={isPendingRepliesLoading}
          isSkippedRepliesLoading={isSkippedRepliesLoading}
          personaId={personaId}
          personaData={personaData}
          automatedReplies={automatedReplies}
          handleApprove={handleApprove}
          handleReject={handleReject}
        />
      </div>
    </Container>
  );
}

export default RepliesPageContainer;
